'use client'

import { useCallback } from 'react'
import { useRouter } from 'next/navigation'
import { doesSessionExist } from 'supertokens-auth-react/recipe/session'
import { redirectToThirdPartyLogin } from 'supertokens-auth-react/recipe/thirdparty'

import type { ApiErrorPayload } from '@/lib/api-types'
import { ApiError } from '@/lib/api'
import { syncAuthenticatedState } from '@/lib/auth-session-bootstrap'
import { APP_ROUTES } from '@/lib/constants'
import { recordAuthDebug } from '@/lib/auth-debug'
import { getAuthRoute } from '@/lib/supertokens-config'
import { logoutAndClearClientState } from '@/lib/logout'
import { captureTracewayException, recordTracewayAuthEvent } from '@/lib/traceway-breadcrumbs'
import { useAuthStore } from '@/stores/auth-store'
import { setPendingThirdPartyProfile } from '@/lib/pending-third-party-profile'

export function useAuth() {
  const router = useRouter()
  const auth = useAuthStore()

  const bootstrap = useCallback(async () => {
    const currentState = useAuthStore.getState()

    if (!currentState.hasHydratedFromStorage) {
      return
    }

    if (currentState.isVerifyingSession) {
      return
    }

    // Dev-only auth bypass: skip SuperTokens session check and sync from API directly
    const devBypass = process.env.NEXT_PUBLIC_DEV_AUTH_BYPASS === 'true'
    recordTracewayAuthEvent('bootstrap start', {
      devBypass,
      hasHydratedFromStorage: currentState.hasHydratedFromStorage,
      status: currentState.status,
    })

    // Trust the cache when it already has a fully authenticated state. Do a
    // silent background revalidation, but never destroy cookies on transient
    // failures — only on a definitive 401 from the backend.
    if (currentState.status === 'authenticated' && currentState.user && !devBypass) {
      void (async () => {
        try {
          await syncAuthenticatedState()
        } catch (error) {
          if (error instanceof ApiError && error.status === 401) {
            recordAuthDebug('auth:bootstrap-silent-401', {
              errorMessage: error.message,
            })
            recordTracewayAuthEvent('bootstrap silent 401', {
              status: error.status,
            })
            await logoutAndClearClientState()
          } else {
            recordAuthDebug('auth:bootstrap-silent-transient-error', {
              errorMessage: error instanceof Error ? error.message : 'unknown_error',
            })
            recordTracewayAuthEvent('bootstrap silent transient error', {
              errorMessage: error instanceof Error ? error.message : 'unknown_error',
            })
            void captureTracewayException(error, {
              boundary: 'auth-bootstrap-silent',
              status: error instanceof ApiError ? error.status : undefined,
            })
            // Transient (network, 5xx). Leave cached auth intact.
          }
        }
      })()
      return
    }

    useAuthStore.setState({ isVerifyingSession: true })

    let hasSession = false
    try {
      hasSession = devBypass || (await doesSessionExist())
    } catch (error) {
      recordAuthDebug('auth:bootstrap-session-check-failed', {
        errorMessage: error instanceof Error ? error.message : 'unknown_error',
      })
      recordTracewayAuthEvent('bootstrap session check failed', {
        errorMessage: error instanceof Error ? error.message : 'unknown_error',
      })
      void captureTracewayException(error, {
        boundary: 'auth-bootstrap-session-check',
      })
      // Transient: don't sign out. Just mark guest locally so the auth page renders.
      useAuthStore.getState().setGuest()
      return
    }

    if (!hasSession) {
      recordAuthDebug('auth:bootstrap-no-session')
      recordTracewayAuthEvent('bootstrap no session', {})
      useAuthStore.getState().setGuest()
      return
    }

    try {
      await syncAuthenticatedState()
    } catch (error) {
      recordAuthDebug('auth:bootstrap-sync-failed', {
        errorMessage: error instanceof Error ? error.message : 'unknown_error',
        status: error instanceof ApiError ? error.status : undefined,
      })
      recordTracewayAuthEvent('bootstrap sync failed', {
        errorMessage: error instanceof Error ? error.message : 'unknown_error',
        status: error instanceof ApiError ? error.status : undefined,
      })
      void captureTracewayException(error, {
        boundary: 'auth-bootstrap-sync',
        status: error instanceof ApiError ? error.status : undefined,
      })
      if (error instanceof ApiError && error.status === 401) {
        // Definitive session-invalid: clear cookies so we don't loop.
        await logoutAndClearClientState()
      } else {
        // Transient: leave cookies intact, just render as guest.
        useAuthStore.getState().setGuest()
      }
    }
  }, [])

  const signin = useCallback(
    async (_payload: { email: string; password: string }) => {
      router.push(getAuthRoute())
    },
    [router],
  )

  const signinWithGoogle = useCallback(
    async (_payload: {
      id_token: string
      email?: string
      display_name?: string
      avatar_url?: string
      sub?: string
    }) => {
      setPendingThirdPartyProfile({
        provider: 'google',
        email: _payload.email,
        display_name: _payload.display_name,
        avatar_url: _payload.avatar_url,
        sub: _payload.sub,
      })
      await redirectToThirdPartyLogin({
        thirdPartyId: 'google',
      })
    },
    [],
  )

  const signup = useCallback(
    async (_payload: { birth_name: string; email: string; password: string }) => {
      router.push(getAuthRoute())
    },
    [router],
  )

  const resendVerificationEmail = useCallback(async (_email: string) => {
    router.push(getAuthRoute())
  }, [router])

  const logout = useCallback(async () => {
    await logoutAndClearClientState()
    router.replace(APP_ROUTES.login)
  }, [router])

  const humanizeError = useCallback((error: unknown) => {
    if (error instanceof ApiError) {
      const envelope: ApiErrorPayload = {
        code: error.code,
        message: error.message,
        details: error.details,
      }
      return envelope
    }

    return {
      code: 'internal_error',
      message: 'Something went wrong',
    }
  }, [])

  return {
    ...auth,
    bootstrap,
    signin,
    signinWithGoogle,
    signup,
    logout,
    resendVerificationEmail,
    humanizeError,
  }
}
